import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ArrowLeft, ExternalLink, RefreshCw, Info, TrendingUp, Briefcase, Globe, Loader2 } from 'lucide-react';
import api from '../services/api';
import AnswerIntelligence from '../components/AnswerIntelligence';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

const KeywordDetails = () => {
  const { keywordId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [showRawOutput, setShowRawOutput] = useState(false);

  useEffect(() => {
    fetchKeywordDetails();
  }, [keywordId]);

  const fetchKeywordDetails = async () => {
    try {
      const response = await api.get(`/keywords/${keywordId}/details`);
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch keyword details');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await api.post(`/queries/run`, {
        keywordId: keywordId
      });

      // Wait a bit then refresh the data
      setTimeout(() => {
        fetchKeywordDetails();
        setRefreshing(false);
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to refresh keyword');
      setRefreshing(false);
    }
  };

  const getBrandVariant = (classification) => {
    switch (classification) {
      case 'owned':
      case 'brand': // backwards compatibility
        return 'default';
      case 'brand_mention':
        return 'default';
      case 'competitor':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const getBrandIcon = (classification) => {
    switch (classification) {
      case 'owned':
      case 'brand': // backwards compatibility
        return <TrendingUp className="h-3 w-3" />;
      case 'brand_mention':
        return <TrendingUp className="h-3 w-3" />;
      case 'competitor':
        return <Briefcase className="h-3 w-3" />;
      default:
        return <Globe className="h-3 w-3" />;
    }
  };

  const getQualityVariant = (score) => {
    if (score >= 9) return 'default';
    if (score >= 7) return 'secondary';
    return 'destructive';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">Keyword Details</h1>
        </div>
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">Keyword Details</h1>
        </div>
        <Alert>
          <AlertDescription>No data available for this keyword</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">"{data.keyword.keyword}"</h1>
        </div>
        <Button
          variant="outline"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          {refreshing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Running...
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh Data
            </>
          )}
        </Button>
      </div>

      <AnswerIntelligence run={data.latestRun} />
      {/* Website & Keyword Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Keyword Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Website: {data.website.name} ({data.website.domain})
            </p>
            <p className="text-sm text-muted-foreground">
              Status: {data.keyword.isActive ? 'Active' : 'Inactive'}
            </p>
            <p className="text-sm text-muted-foreground">
              Total Runs: {data.keyword.totalRuns}
            </p>
            {data.keyword.lastRunAt && (
              <p className="text-sm text-muted-foreground">
                Last Run: {format(new Date(data.keyword.lastRunAt), 'PPp')}
              </p>
            )}
          </CardContent>
        </Card>

        {data.latestRun && (
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Latest Run Results</CardTitle>
                <Badge variant={data.latestRun.status === 'completed' ? 'default' : 'secondary'}>
                  {data.latestRun.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {data.latestRun.status === 'completed' && (
                <>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Total Citations:</span>
                    <span className="text-sm font-bold">{data.latestRun.totalCitations}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Owned Citations:</span>
                    <span className="text-sm font-bold text-green-600">{data.latestRun.ownedCitations || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Brand Mention Citations:</span>
                    <span className="text-sm font-bold text-emerald-600">{data.latestRun.brandMentionCitations || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Total Brand Mentions:</span>
                    <span className="text-sm font-bold text-emerald-600">{data.latestRun.totalBrandMentions || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Competitor Citations:</span>
                    <span className="text-sm font-bold text-orange-600">{data.latestRun.competitorCitations}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Other Citations:</span>
                    <span className="text-sm font-bold">{data.latestRun.otherCitations}</span>
                  </div>

                  {data.latestRun.estimatedCostUsd !== undefined && (
                    <>
                      <div className="border-t pt-2 mt-2"></div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">API Cost:</span>
                        <span className="text-sm font-bold text-blue-600">
                          ${Number(data.latestRun.estimatedCostUsd).toFixed(4)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-muted-foreground">Tokens Used:</span>
                        <span className="text-xs">
                          {data.latestRun.totalTokens?.toLocaleString() || 0}
                        </span>
                      </div>
                    </>
                  )}
                </>
              )}

              {data.latestRun.completedAt && (
                <p className="text-sm text-muted-foreground">
                  Completed: {format(new Date(data.latestRun.completedAt), 'PPp')}
                </p>
              )}

              {data.latestRun.rawOutput && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowRawOutput(true)}
                  className="mt-2"
                >
                  <Info className="mr-2 h-4 w-4" />
                  View Raw Output
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Citations Table */}
      {data.citations && data.citations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Citations Found ({data.citations.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Domain</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead className="w-32">Type</TableHead>
                    <TableHead className="w-24">Brand Mentions</TableHead>
                    <TableHead className="w-24">Sentiment</TableHead>
                    <TableHead className="w-16">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.citations.map((citation, index) => (
                    <TableRow key={citation.id || index}>
                      <TableCell>{citation.position || index + 1}</TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm font-medium">{citation.domain}</p>
                          <div className="flex gap-1 mt-1">
                            {citation.isTechRadar && (
                              <Badge variant="default">TechRadar</Badge>
                            )}
                            {citation.isPrioritySource && !citation.isTechRadar && (
                              <Badge variant="secondary">Priority</Badge>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm">{citation.title}</p>
                          {citation.snippet && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {citation.snippet.length > 100
                                ? `${citation.snippet.substring(0, 100)}...`
                                : citation.snippet
                              }
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getBrandVariant(citation.brand_classification || citation.brandClassification)}>
                          <span className="flex items-center gap-1">
                            {getBrandIcon(citation.brand_classification || citation.brandClassification)}
                            {citation.brand_classification || citation.brandClassification || 'other'}
                          </span>
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {(citation.brandMentionCount || citation.brand_mention_count) > 0 ? (
                          <Badge variant="default" className="bg-emerald-600">
                            {citation.brandMentionCount || citation.brand_mention_count || 0}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">0</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const s = citation.sentiment;
                          if (!s) return <span className="text-muted-foreground text-xs">—</span>;
                          const styles = {
                            positive: 'bg-emerald-100 text-emerald-800 border-emerald-200',
                            neutral: 'bg-slate-100 text-slate-700 border-slate-200',
                            negative: 'bg-red-100 text-red-800 border-red-200'
                          };
                          return (
                            <Badge variant="outline" className={styles[s] || ''}>
                              {s}
                            </Badge>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => window.open(citation.url, '_blank')}
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Open in new tab</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {data.citations && data.citations.length === 0 && data.latestRun?.status === 'completed' && (
        <Alert>
          <AlertDescription>
            No citations found for this keyword. Try refreshing the data.
          </AlertDescription>
        </Alert>
      )}

      {!data.latestRun && (
        <Alert>
          <AlertDescription>
            This keyword hasn't been run yet. Click "Refresh Data" to run it for the first time.
          </AlertDescription>
        </Alert>
      )}

      {/* Raw Output Dialog */}
      <Dialog open={showRawOutput} onOpenChange={setShowRawOutput}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Raw AI Output</DialogTitle>
          </DialogHeader>
          <pre className="whitespace-pre-wrap font-mono text-sm bg-muted p-4 rounded-lg max-h-[60vh] overflow-auto">
            {data.latestRun?.rawOutput || 'No raw output available'}
          </pre>
          <DialogFooter>
            <Button onClick={() => setShowRawOutput(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KeywordDetails;
